import { http } from '@/api/Connect';
import { API_VERSION, BASE_WEB_OPERATOR } from '@/api/CopyStake/BaseUrls';
import { AxiosResponse } from 'axios';

import {
  EStreamingType,
  ICopyStakeStatsResponse,
  TCopyStakeSettingsAutoclickerUrlPayload,
  TCopyStakeSettingsAutoclickerUrlResponse,
  TCopyStakeSettingsBetVolumesPayload,
  TCopyStakeSettingsBetVolumesResponse,
  TCopyStakeSettingsInactiveTimeoutPayload,
  TCopyStakeSettingsInactiveTimeoutResponse,
  TCopyStakeSettingsInitialBalanceUndergroundCasinoResponse,
  TCopyStakeSettingsLimitsPayload,
  TCopyStakeSettingsLimitsResponse,
  TCopyStakeSettingsLiveKitCredentialsPayload,
  TCopyStakeSettingsLiveKitCredentialsResponse,
  TCopyStakeSettingsRestreamPayload,
  TCopyStakeSettingsRestreamResponse,
  TCopyStakeSettingsShowOnlyStreamersPayload,
  TCopyStakeSettingsShowOnlyStreamersResponse
} from '@/api/schema';

const API_WEB_SETTINGS = (version: string = API_VERSION.V1): string =>
  `${BASE_WEB_OPERATOR(version)}/settings`;

export async function getBetVolumesConfiguration(): Promise<TCopyStakeSettingsBetVolumesResponse> {
  return http
    .get(`${API_WEB_SETTINGS()}/bet-volumes`)
    .then(
      ({ data }: AxiosResponse<TCopyStakeSettingsBetVolumesResponse>) => data
    );
}

export async function updateBetVolumesConfiguration(
  payload: TCopyStakeSettingsBetVolumesPayload
): Promise<TCopyStakeSettingsBetVolumesResponse> {
  return http
    .patch(`${API_WEB_SETTINGS()}/bet-volumes`, payload)
    .then(
      ({ data }: AxiosResponse<TCopyStakeSettingsBetVolumesResponse>) => data
    );
}

export async function getLiveKitCredentialsConfiguration(): Promise<TCopyStakeSettingsLiveKitCredentialsResponse> {
  return http
    .get(`${API_WEB_SETTINGS()}/livekit`)
    .then(
      ({ data }: AxiosResponse<TCopyStakeSettingsLiveKitCredentialsResponse>) =>
        data
    );
}

export async function updateLiveKitCredentialsConfiguration(
  payload: TCopyStakeSettingsLiveKitCredentialsPayload
): Promise<TCopyStakeSettingsLiveKitCredentialsResponse> {
  return http
    .patch(`${API_WEB_SETTINGS()}/livekit`, payload)
    .then(
      ({ data }: AxiosResponse<TCopyStakeSettingsLiveKitCredentialsResponse>) =>
        data
    );
}

export async function getAutoclickerUrlConfiguration(): Promise<TCopyStakeSettingsAutoclickerUrlResponse> {
  return http
    .get(`${API_WEB_SETTINGS()}/urls`)
    .then(
      ({ data }: AxiosResponse<TCopyStakeSettingsAutoclickerUrlResponse>) =>
        data
    );
}

export async function updateAutoclickerUrlConfiguration(
  payload: TCopyStakeSettingsAutoclickerUrlPayload
): Promise<TCopyStakeSettingsAutoclickerUrlResponse> {
  return http
    .patch(`${API_WEB_SETTINGS()}/urls`, payload)
    .then(
      ({ data }: AxiosResponse<TCopyStakeSettingsAutoclickerUrlResponse>) =>
        data
    );
}

export async function getShowOnlyStreamersConfiguration(): Promise<TCopyStakeSettingsShowOnlyStreamersResponse> {
  return http
    .get(`${API_WEB_SETTINGS()}/show-only-streamers`)
    .then(
      ({ data }: AxiosResponse<TCopyStakeSettingsShowOnlyStreamersResponse>) =>
        data
    );
}

export async function updateShowOnlyStreamersConfiguration(
  payload: TCopyStakeSettingsShowOnlyStreamersPayload
): Promise<void> {
  return http
    .patch(`${API_WEB_SETTINGS()}/show-only-streamers`, null, {
      params: payload
    })
    .then(({ data }: AxiosResponse<void>) => data);
}

export async function getInactivityTimeoutsConfiguration(): Promise<TCopyStakeSettingsInactiveTimeoutResponse> {
  return http
    .get(`${API_WEB_SETTINGS()}/inactivity-timeout`)
    .then(
      ({ data }: AxiosResponse<TCopyStakeSettingsInactiveTimeoutResponse>) =>
        data
    );
}

export async function updateInactivityTimeoutsConfiguration(
  payload: TCopyStakeSettingsInactiveTimeoutPayload
): Promise<TCopyStakeSettingsInactiveTimeoutResponse> {
  return http
    .patch(`${API_WEB_SETTINGS()}/inactivity-timeout`, payload)
    .then(
      ({ data }: AxiosResponse<TCopyStakeSettingsInactiveTimeoutResponse>) =>
        data
    );
}

export async function getRestreamConfiguration(): Promise<TCopyStakeSettingsRestreamResponse> {
  return http
    .get(`${API_WEB_SETTINGS()}/restream`)
    .then(
      ({ data }: AxiosResponse<TCopyStakeSettingsRestreamResponse>) => data
    );
}

export async function updateRestreamConfiguration(
  payload: TCopyStakeSettingsRestreamPayload
): Promise<TCopyStakeSettingsRestreamResponse> {
  return http
    .patch(`${API_WEB_SETTINGS()}/restream`, payload)
    .then(
      ({ data }: AxiosResponse<TCopyStakeSettingsRestreamResponse>) => data
    );
}

export async function getLimitsConfiguration(
  type: EStreamingType
): Promise<TCopyStakeSettingsLimitsResponse> {
  return http
    .get(`${API_WEB_SETTINGS()}/limits`, {
      ...{ params: { type } },
      cacheTime: 0
    })
    .then(({ data }: AxiosResponse<TCopyStakeSettingsLimitsResponse>) => data);
}

export async function updateLimitsConfiguration(
  payload: TCopyStakeSettingsLimitsPayload,
  type: EStreamingType
): Promise<TCopyStakeSettingsLimitsResponse> {
  return http
    .patch(`${API_WEB_SETTINGS()}/limits`, payload, {
      params: {
        type
      }
    })
    .then(({ data }: AxiosResponse<TCopyStakeSettingsLimitsResponse>) => data);
}

export async function getStreamerOnlineLimits(): Promise<ICopyStakeStatsResponse> {
  return http
    .get(
      `${API_WEB_SETTINGS()}/online-limits?streamingType=${EStreamingType.WHIP}`
    )

    .then(({ data }: AxiosResponse): ICopyStakeStatsResponse => data);
}

export async function getWebStreamerOnlineLimits(): Promise<ICopyStakeStatsResponse> {
  return http
    .get(
      `${API_WEB_SETTINGS()}/online-limits?streamingType=${EStreamingType.WEB}`
    )
    .then(({ data }: AxiosResponse): ICopyStakeStatsResponse => data);
}

export async function getShowCredentialsConfiguration(): Promise<boolean> {
  return http
    .get(`${API_WEB_SETTINGS()}/show-credentials`)
    .then(({ data }: AxiosResponse<boolean>) => data);
}

export async function getInitialBalanceUndergroundCasinoConfiguration(): Promise<TCopyStakeSettingsInitialBalanceUndergroundCasinoResponse> {
  return http
    .get(`${API_WEB_SETTINGS()}/uc-streamer-balance`)
    .then(
      ({
        data
      }: AxiosResponse<TCopyStakeSettingsInitialBalanceUndergroundCasinoResponse>) =>
        data
    );
}

export async function updateInitialBalanceUndergroundCasinoConfiguration(
  payload: TCopyStakeSettingsInitialBalanceUndergroundCasinoResponse
): Promise<TCopyStakeSettingsInitialBalanceUndergroundCasinoResponse> {
  return http
    .patch(`${API_WEB_SETTINGS()}/uc-streamer-balance`, payload)
    .then(
      ({
        data
      }: AxiosResponse<TCopyStakeSettingsInitialBalanceUndergroundCasinoResponse>) =>
        data
    );
}
